import axiosInstance from '../config/axios.js';
import {
  PDF_RESOLVE_CHUNK_SIZE,
  buildPdfPageChunks,
  estimatePdfPageCount,
  mergeQrResolveChunkResponses,
} from '../utils/pdfScanChunks.js';

const API_BASE = '/shipment-crossings';

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');

const appendScanFile = (formData, file) => {
  if (!(file instanceof File)) return;
  formData.append(isPdfFile(file) ? 'scanned_pdf' : 'scanned_image', file, file.name);
};

const QR_UPLOAD_TIMEOUT_MS = 90000;

const postResolveFile = async (file, { action, pageStart, pageEnd } = {}) => {
  const formData = new FormData();
  appendScanFile(formData, file);
  if (action) formData.append('action', action);
  if (pageStart != null) formData.append('pdf_page_start', String(pageStart));
  if (pageEnd != null) formData.append('pdf_page_end', String(pageEnd));
  const response = await axiosInstance.post(`${API_BASE}/resolve`, formData, {
    headers: { 'Content-Type': undefined },
    timeout: QR_UPLOAD_TIMEOUT_MS,
  });
  return response.data;
};

const resolvePdfInChunks = async (file, { action } = {}) => {
  const buffer = await file.arrayBuffer();
  const pageCount = estimatePdfPageCount(buffer);
  const chunks = buildPdfPageChunks(pageCount, PDF_RESOLVE_CHUNK_SIZE);
  if (chunks.length === 1) {
    const only = chunks[0];
    return postResolveFile(file, {
      action,
      pageStart: pageCount > 1 ? only.start : undefined,
      pageEnd: pageCount > 1 ? only.end : undefined,
    });
  }
  const parts = [];
  for (const chunk of chunks) {
    parts.push(await postResolveFile(file, {
      action,
      pageStart: chunk.start,
      pageEnd: chunk.end,
    }));
  }
  return mergeQrResolveChunkResponses(parts);
};

const shipmentCrossingService = {
  /** Resolve shipment(s) for crossing from a scanned image/PDF file. */
  resolveFromFile: async (file, { action } = {}) => {
    if (isPdfFile(file)) {
      return resolvePdfInChunks(file, { action });
    }
    return postResolveFile(file, { action });
  },

  /** Resolve from a live QR decode (raw QR string or shipment id) or explicit id(s). */
  resolveFromData: async ({ qrData, shipmentId, shipmentIds, unitNo, action } = {}) => {
    const payload = {};
    // Always send raw QR when present so parcel unit (?u=N) is not dropped.
    if (qrData) payload.qr_data = qrData;
    if (shipmentIds?.length) payload.shipment_ids = shipmentIds;
    else if (shipmentId) payload.shipment_id = shipmentId;
    if (unitNo != null && unitNo !== '') payload.unit_no = unitNo;
    if (!payload.qr_data && !payload.shipment_id && !payload.shipment_ids) {
      throw new Error('No QR data to resolve');
    }
    if (action) payload.action = action;

    const response = await axiosInstance.post(`${API_BASE}/resolve`, payload);
    return response.data;
  },

  /** Searchable active vehicle list for the crossing vehicle selector. */
  listVehicles: async ({ search } = {}) => {
    const response = await axiosInstance.get(`${API_BASE}/vehicles`, {
      params: search ? { search } : {},
    });
    return response.data;
  },

  /** ML sheets the current user can filter (action: unload | dispatch). */
  listCrossableMlSheets: async ({ action } = {}) => {
    const params = {};
    if (action) params.action = action;
    const response = await axiosInstance.get(`${API_BASE}/ml-sheets`, { params });
    return response.data;
  },

  /** Paginated shipment list for unload or dispatch-again. */
  listCrossableShipments: async ({ page, perPage, search, mlSheetId, action } = {}) => {
    const params = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;
    if (search) params.search = search;
    if (mlSheetId) params.ml_sheet_id = mlSheetId;
    if (action) params.action = action;
    const response = await axiosInstance.get(`${API_BASE}/shipments`, { params });
    return response.data;
  },

  /** Mark dispatched parcels missing at the crossing hub or destination. Status stays Dispatched. */
  markMissing: async ({ shipmentIds, note, source } = {}) => {
    const response = await axiosInstance.post(`${API_BASE}/shortfalls`, {
      shipment_ids: shipmentIds,
      note,
      source: source || 'crossing',
    });
    return response.data;
  },

  /** Open crossing shortfalls for the Missing tab. */
  listShortfalls: async ({ search, mlSheetId } = {}) => {
    const params = {};
    if (search) params.search = search;
    if (mlSheetId) params.ml_sheet_id = mlSheetId;
    const response = await axiosInstance.get(`${API_BASE}/shortfalls`, { params });
    return response.data;
  },

  /** Unload at crossing hub (Dispatched → Received). Operational only. */
  unloadShipments: async ({
    shipmentIds,
    remarks,
    verified_units_by_shipment,
    allow_legacy_qr,
  } = {}) => {
    const payload = { shipment_ids: shipmentIds };
    if (remarks) payload.remarks = remarks;
    if (verified_units_by_shipment) {
      payload.verified_units_by_shipment = verified_units_by_shipment;
    }
    if (allow_legacy_qr) payload.allow_legacy_qr = true;
    const response = await axiosInstance.post(`${API_BASE}/unload`, payload);
    return response.data;
  },

  /** Dispatch again: create a crossing onto a vehicle (requires Received). */
  createCrossing: async ({
    shipmentIds,
    newVehicleId,
    remarks,
    crossingCharge,
    verified_units_by_shipment,
    allow_legacy_qr,
  } = {}) => {
    const payload = {
      shipment_ids: shipmentIds,
      new_vehicle_id: newVehicleId,
    };
    if (remarks) payload.remarks = remarks;
    if (crossingCharge !== undefined && crossingCharge !== null && crossingCharge !== '') {
      payload.crossing_charge = crossingCharge;
    }
    if (verified_units_by_shipment) {
      payload.verified_units_by_shipment = verified_units_by_shipment;
    }
    if (allow_legacy_qr) payload.allow_legacy_qr = true;
    const response = await axiosInstance.post(`${API_BASE}/`, payload);
    return response.data;
  },

  /** Crossing history (role/company scoped). */
  listCrossings: async ({ page, perPage, search } = {}) => {
    const params = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;
    if (search) params.search = search;
    const response = await axiosInstance.get(`${API_BASE}/`, { params });
    return response.data;
  },
};

export default shipmentCrossingService;
